<template>
  <!-- elevation-panel-v2 (feature-flagged pilot, planned-route mode): manages
       its own bottom-sheet chrome internally, so it doesn't use the
       collapsed/expanded state below (that stays wired to the fallback only).
       Real surface data comes from each MultiLineString feature's
       `properties.surfaces` range-compressed array (see `allSurfaces`),
       expanded to a flat per-point array parallel to `allCoordinates`. -->
  <ElevationPanelV2
    v-if="elevationPanelV2Enabled && chartData"
    mode="planned"
    :coordinates="allCoordinates"
    :surfaces="allSurfaces"
    :max-points="200"
    :get-map="mapGetter"
    :stats="stats"
    :dismissible="dismissible"
    class="hike-map-elevation-v2"
    @dismiss="$emit('dismiss')"
  />
  <div v-else class="elevation-profile-container" :class="{ collapsed: isCollapsed, expanded: isExpanded }">
    <ElevationProfile
      :data="chartData"
      :highlights="projectedHighlights"
      :title="profileTitle"
      :subtitle="viewSubtitle"
      :stats="stats"
      :get-map="mapGetter"
      :max-points="200"
      collapsible
      expandable
      :collapsed="isCollapsed"
      :expanded="isExpanded"
      @update:collapsed="isCollapsed = $event"
      @update:expanded="onExpandedChange"
      @highlight-click="onHighlightClick"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref';
import ElevationProfile from '../components/ElevationProfile.vue';
import ElevationPanelV2 from '../elevation_panel_v2/ElevationPanelV2.vue';
import {
  buildElevationChartData,
  type ElevationChartData,
} from '../../utils/elevation/buildElevationChartData';
import {
  expandSurfaceRanges,
  multiLineStringPointCount,
} from '../../utils/elevation/expandSurfaceRanges';
import {
  projectHighlightsOntoLine,
  type ProjectedHighlight,
} from '../../utils/elevation/projectHighlights';

interface ElevationStats {
  distanceMeters: number;
  gainMeters: number;
  lossMeters: number;
  durationSeconds: number;
}

export default defineComponent({
  name: 'HikeMapElevationProfile',
  components: { ElevationProfile, ElevationPanelV2 },
  props: {
    geojson: {
      type: Object,
      default: null
    },
    activeView: {
      type: Object,
      default: () => ({ type: 'full' })
    },
    plannedTrails: {
      type: Array,
      default: () => []
    },
    hikeDays: {
      type: Array,
      default: () => []
    },
    // Hike highlights / waypoints to project onto the profile. Each item is the
    // raw hike-highlight shape ({ id/clientId, title, coordinates: [lon, lat],
    // dayNumber, ... }). Defaults to [] so parents that don't supply them keep
    // working. The parent (HikeMap.vue) should pass `:highlights="hikeHighlights"`.
    highlights: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    // Forwarded to ElevationPanelV2's `dismissible` prop — see its doc
    // comment. Only meaningful in elevation-panel-v2 mode; ignored by the
    // fallback ElevationProfile path.
    dismissible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['dismiss'],
  data() {
    return {
      isCollapsed: false,
      isExpanded: false
    };
  },
  computed: {
    // Staged rollout gate for the elevation-panel-v2 redesign (hike_map
    // pilot, planned-route mode). See AppSetting.elevation_panel_v2_enabled?.
    elevationPanelV2Enabled(): boolean {
      return !!(window as any).elevationPanelV2Enabled;
    },

    profileTitle(): string {
      if (this.activeView.type === 'day') {
        const dayInfo = this.hikeDays.find((d: any) => d.dayNumber === this.activeView.dayNumber) as any;
        return dayInfo?.name || `Day ${this.activeView.dayNumber}`;
      } else if (this.activeView.type === 'route') {
        const trail = this.getTrailById(this.activeView.trailId);
        return trail?.title || 'Route';
      }
      return 'Elevation';
    },

    viewSubtitle(): string {
      if (this.activeView.type === 'day') {
        const dayInfo = this.hikeDays.find((d: any) => d.dayNumber === this.activeView.dayNumber) as any;
        return dayInfo?.description || '';
      }
      return '';
    },

    // Build an ordering map from ptv2PlannedTrailId -> sort index using the
    // plannedTrails prop (same data source as the left panel, ordered by day then position).
    trailOrderMap(): Map<string, number> {
      const map = new Map<string, number>();
      [...(this.plannedTrails as any[])]
        .sort((a, b) => ((a.day ?? 0) - (b.day ?? 0)) || ((a.position ?? 0) - (b.position ?? 0)))
        .forEach((t, idx) => {
          const id = t.ptv2PlannedTrail?.id ?? t.id;
          if (id != null) map.set(String(id), idx);
        });
      return map;
    },

    filteredFeatures(): any[] {
      if (!this.geojson?.features) return [];

      let features = this.geojson.features;

      if (this.activeView.type === 'day') {
        // For day view: show only 'route' type within that day (exclude alt, spur, transport)
        features = features.filter((f: any) =>
          f.properties?.hikeDayNumber === this.activeView.dayNumber &&
          f.properties?.routeStyle === 'route'
        );
      } else if (this.activeView.type === 'route') {
        // For individual route: show just that route (any type including alt, spur, transport)
        features = features.filter((f: any) =>
          f.properties?.ptv2PlannedTrailId != null &&
          String(f.properties.ptv2PlannedTrailId) === String(this.activeView.trailId)
        );
      } else {
        // For full view: show only 'route' type (exclude alt, spur, transport)
        features = features.filter((f: any) =>
          f.properties?.routeStyle === 'route'
        );
      }

      // Sort features to match the left panel's day/position sequence.
      // The trailOrderMap is built from plannedTrails (hikesPlannedTrails join table data)
      // which is the authoritative ordering — the same data the left panel uses.
      if (this.trailOrderMap.size > 0) {
        features = [...features].sort((a: any, b: any) => {
          const aIdx = this.trailOrderMap.get(String(a.properties?.ptv2PlannedTrailId)) ?? 9999;
          const bIdx = this.trailOrderMap.get(String(b.properties?.ptv2PlannedTrailId)) ?? 9999;
          return aIdx - bIdx;
        });
      }

      return features;
    },

    multiLineStrings(): any[] {
      return this.filteredFeatures.filter(f => f.geometry?.type === 'MultiLineString');
    },

    // Flattened `[lon, lat, (ele)]` coordinates for the active view, in feature
    // (day/position) order.
    allCoordinates(): number[][] {
      const coords: number[][] = [];
      this.multiLineStrings.forEach(feature => {
        if (feature.geometry?.coordinates) {
          feature.geometry.coordinates.forEach((lineString: number[][]) => {
            coords.push(...lineString);
          });
        }
      });
      return coords;
    },

    // elevation-panel-v2 only: flat per-point surface-integer array parallel
    // to `allCoordinates`. Each MultiLineString feature's `properties.surfaces`
    // is range-compressed (`[[startIndex, endIndex, surfaceInt], ...]`,
    // relative to that feature's own flattened point sequence — see
    // `hike_feature_collection.rb`); expand each feature's ranges in the same
    // per-feature/per-lineString order `allCoordinates` concatenates points
    // in, so the two arrays line up index-for-index.
    allSurfaces(): (number | undefined)[] {
      const surfaces: (number | undefined)[] = [];
      this.multiLineStrings.forEach((feature: any) => {
        surfaces.push(...expandSurfaceRanges(
          feature.properties?.surfaces,
          multiLineStringPointCount(feature.geometry?.coordinates)
        ));
      });
      return surfaces;
    },

    // Delegate the actual profile computation (equal-distance downsampling,
    // 6m-threshold gain/loss, linear-distance points) to the shared builder.
    chartData(): ElevationChartData | null {
      const coords = this.allCoordinates;
      if (!coords.length) return null;
      return buildElevationChartData({
        coordinates: coords,
        maxPoints: 200,
        measurementPref: MeasurementPrefUtility.preference()
      });
    },

    // Header stats. Gain/loss come from the shared builder (6m-threshold),
    // NOT the old naive positive-delta sum — this keeps gain consistent with
    // the planner.
    stats(): ElevationStats {
      const d = this.chartData;
      return {
        distanceMeters: d?.maxDistanceMeters ?? 0,
        gainMeters: d?.elevationGainMeters ?? 0,
        lossMeters: d?.elevationLossMeters ?? 0,
        durationSeconds: this.totalDurationSeconds
      };
    },

    totalDurationSeconds(): number {
      if (this.activeView.type === 'route') {
        // For individual route: show duration regardless of type
        const trail = this.getTrailById(this.activeView.trailId);
        return (trail?.calculatedDurationMinutes || 0) * 60;
      }

      // Day / full: sum only main 'route' segment durations (exclude alt, spur, transport).
      let duration = 0;
      this.plannedTrails.forEach((trail: any) => {
        if (this.activeView.type === 'day' && trail.day !== this.activeView.dayNumber) return;
        const plannedTrail = trail.ptv2PlannedTrail || trail;
        if (plannedTrail.routeStyle === 'route') {
          duration += (plannedTrail.calculatedDurationMinutes || 0);
        }
      });
      return duration * 60;
    },

    // Project hike highlights onto the active line. Highlights far from the line
    // are dropped by projectHighlightsOntoLine, so passing all of them is safe.
    projectedHighlights(): ProjectedHighlight[] {
      const coords = this.allCoordinates;
      const highlights = this.highlights as any[];
      if (coords.length < 2 || !highlights?.length) return [];

      const inputs = highlights
        .map((h) => {
          const c = h?.coordinates || [];
          return {
            id: h?.id ?? h?.clientId,
            name: h?.title || '',
            lon: c[0],
            lat: c[1],
            icon: undefined as string | undefined
          };
        })
        .filter((i) => typeof i.lon === 'number' && typeof i.lat === 'number' && i.id != null);

      return projectHighlightsOntoLine(coords, inputs, {
        maxDistanceMeters: this.chartData?.maxDistanceMeters
      });
    }
  },
  methods: {
    getTrailById(trailId: string | number | undefined): any {
      for (const trail of this.plannedTrails) {
        if ((trail as any).ptv2PlannedTrail?.id === trailId) {
          return (trail as any).ptv2PlannedTrail;
        } else if ((trail as any).id === trailId) {
          return trail;
        }
      }
      return null;
    },

    // Lazy map accessor handed to the shared component so it manages the
    // .elevation-marker on hover. Bound to the instance by Vue.
    mapGetter(): any {
      return (window as any).map ?? null;
    },

    onExpandedChange(value: boolean) {
      this.isExpanded = value;
    },

    onHighlightClick(highlight: ProjectedHighlight) {
      const map = (window as any).map;
      if (!map || typeof map.flyTo !== 'function') return;
      try {
        map.flyTo({
          center: highlight.snappedLngLat,
          zoom: Math.max(typeof map.getZoom === 'function' ? map.getZoom() : 13, 13)
        });
      } catch (error) {
        console.error('Error panning to highlight:', error);
      }
    }
  }
});
</script>

<style scoped>
/* elevation-panel-v2 manages its own height/collapse state internally (see
   BottomSheet.vue) — this only owns desktop positioning, matching the
   fallback panel's footprint so the flag toggle doesn't shift the layout. */
.hike-map-elevation-v2 {
  position: absolute;
  bottom: 40px;
  left: 20px;
  right: 20px;
  margin: auto;
  max-width: 630px;
  z-index: 999;
}

@media (max-width: 768px) {
  .hike-map-elevation-v2 {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    max-width: none;
    margin: 0;
  }
}

.elevation-profile-container {
  position: absolute;
  bottom: 40px;
  left: 20px;
  right: 20px;
  margin: auto;
  max-width: 630px;
  height: 180px;
  background: rgba(40, 42, 50, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  z-index: 999;
  transition: all 0.3s ease;
  color: #fff;
}

.elevation-profile-container.collapsed {
  height: 48px;
}

.elevation-profile-container.expanded {
  height: 400px;
  max-width: calc(100% - 40px);
}

/* The shared ElevationProfile provides the header/chart; let the outer
   container own positioning + chrome (bg / shadow / rounded corners / height)
   so existing desktop + mobile-sheet styling keeps working. */
.elevation-profile-container :deep(.elevation-profile) {
  height: 100%;
  background: transparent;
  box-shadow: none;
  border-radius: 12px;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .elevation-profile-container {
    left: 10px;
    right: 10px;
    height: 150px;
  }

  .elevation-profile-container.expanded {
    height: 300px;
  }
}
</style>
